import { forwardRef, useState } from 'react'
import { useObjectRef } from '@react-aria/utils'
import Styles from './ChatInput.module.scss'
import { AttachedImage, ChatInputProps } from './Types'
import { ChatActions } from './ChatActions'
import { ChatImageAttachment } from './ChatImageAttachment'

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    { value, onChange, placeholder, isDisabled, onSubmit, fileId, threadId, hasDesignContext, isProcessing, onAbort },
    fRef,
  ) => {
    const [attachedImage, setAttachedImage] = useState<AttachedImage | null>(null)
    const isValidInput = value.trim().length >= 2

    const textareaRef = useObjectRef<HTMLTextAreaElement>(fRef)

    const onResize = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = ''
        const height = Math.min(200, textareaRef.current.scrollHeight)
        textareaRef.current.style.height = `${height}px`
      }
    }

    const handleSubmit = () => {
      onSubmit({ value, attachedImage })
      setAttachedImage(null)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.stopPropagation()
      }

      if (e.key === 'Enter' && !e.shiftKey && !isDisabled && isValidInput) {
        e.preventDefault()
        handleSubmit()
      }
    }

    return (
      <div className={Styles.inputContainer}>
        <div className={Styles.chatInput}>
          {attachedImage && (
            <ChatImageAttachment imageUrl={attachedImage.url} onRemove={() => setAttachedImage(null)} />
          )}
          <textarea
            className={Styles.textarea}
            ref={textareaRef}
            id="ask-blaze"
            data-testid="ask-blaze"
            onChange={(e) => {
              onChange(e.currentTarget.value)
              onResize()
            }}
            onKeyDown={handleKeyDown}
            value={value}
            name="ask-blaze"
            placeholder={placeholder || 'Ask Blaze anything'}
            aria-label="Ask Blaze anything"
          />
          <ChatActions
            onChange={onChange}
            isDisabled={!isValidInput || !!isDisabled}
            onSubmit={handleSubmit}
            fileId={fileId}
            threadId={threadId}
            hasDesignContext={hasDesignContext}
            onAttachImage={setAttachedImage}
            isProcessing={isProcessing}
            onAbort={onAbort}
          />
        </div>
      </div>
    )
  },
)
